import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadCloud, CheckCircle, XCircle, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { fmtNumber, fmtTons, fmtDay } from '../components/PostCollections/format';
import { c, btn, card } from '../theme';

// Cloud Run refuses a request body over 32 MiB before it reaches the API, and
// the export is ~46 MB for three and a half months. CSV compresses about
// tenfold, so the file is gzipped here, in the browser, and the server reads
// either form — the Customer Billing upload's answer to the same limit.
async function compress(file) {
  if (/\.gz$/i.test(file.name)) return { blob: file, name: file.name };
  if (typeof CompressionStream === 'undefined') return { blob: file, name: file.name };
  const blob = await new Response(file.stream().pipeThrough(new CompressionStream('gzip'))).blob();
  return { blob, name: `${file.name}.gz` };
}

const MAX_SEND_MB = 30;

export default function PostCollectionsUpload() {
  const qc = useQueryClient();
  const [file, setFile]         = useState(null);
  const [stage, setStage]       = useState(null);   // null | 'compressing' | 'uploading' | 'reading'
  const [progress, setProgress] = useState(0);
  const [preview, setPreview]   = useState(null);   // { batch_id, preview }
  const [failure, setFailure]   = useState(null);   // { error, details }
  const inputRef = useRef(null);

  const { data: batches = [], isLoading: batchesLoading } = useQuery({
    queryKey: ['post-collections-batches'],
    queryFn:  () => api.get('/post-collections/batches'),
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      setFailure(null);
      if (/\.xlsx?$/i.test(file.name)) {
        throw new Error('This is an Excel workbook. Save it as CSV (File → Save As → CSV UTF-8) and upload the .csv.');
      }
      setStage('compressing');
      const { blob, name } = await compress(file);
      if (blob.size > MAX_SEND_MB * 1024 * 1024) {
        throw new Error(`Even compressed the file is ${(blob.size / 1024 / 1024).toFixed(0)} MB, over the ${MAX_SEND_MB} MB limit. Export fewer days at a time.`);
      }
      setStage('uploading');
      const fd = new FormData();
      fd.append('file', blob, name);
      return api.upload('/post-collections/upload', fd, (p) => { setProgress(p); if (p >= 100) setStage('reading'); });
    },
    onSuccess: (data) => { setPreview(data); setStage(null); setProgress(0); },
    onError: (err) => {
      setStage(null); setProgress(0);
      setFailure({ error: err.message || 'Upload failed.', details: err.data?.details || [] });
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => api.commit('/post-collections/upload/confirm', { batch_id: preview.batch_id }),
    onSuccess: (data) => {
      toast.success(`Committed ${fmtNumber(data.siteDays)} site-days${data.replaced ? `, ${fmtNumber(data.replaced)} of them replacing earlier uploads` : ''}.`);
      resetAll();
      for (const key of ['post-collections-batches', 'post-collections-sites', 'post-collections-report']) {
        qc.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (err) => toast.error(err.message || 'Confirm failed.'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post('/post-collections/upload/cancel', { batch_id: preview.batch_id }),
    onSuccess: () => { toast('Upload discarded.'); resetAll(); qc.invalidateQueries({ queryKey: ['post-collections-batches'] }); },
    onError: (err) => toast.error(err.message || 'Cancel failed.'),
  });

  function resetAll() {
    setFile(null); setPreview(null); setProgress(0); setStage(null); setFailure(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  const p = preview?.preview;
  const busy = uploadMutation.isPending || confirmMutation.isPending || cancelMutation.isPending;
  const stageText = { compressing: 'Compressing…', uploading: `Uploading ${progress}%`, reading: 'Reading the export…' }[stage];

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Post Collections Upload"
        subtitle="Import the daily productivity export as CSV, with or without its header row, and with its PrevTOF column when it has one. Each row of the export repeats a ticket once for every bale, labor and line row of its day, so the file is taken apart into those pieces before anything is counted. Each site-day in the file replaces that site-day if it was loaded before; other days are left alone."
      />

      {!preview && (
        <div style={s.card}>
          <div
            style={{ ...s.dropZone, borderColor: file ? '#86EFAC' : c.borderStrong }}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
          >
            <input ref={inputRef} id="post-collections-file" type="file" accept=".csv,.gz" style={{ display: 'none' }}
                   onChange={(e) => setFile(e.target.files[0] || null)} />
            <UploadCloud size={26} color={file ? c.success : c.textFaint} />
            <div style={s.dropText}>
              {file ? (
                <><strong style={{ color: c.success }}>{file.name}</strong><br /><span style={{ color: c.textFaint }}>{(file.size / 1024 / 1024).toFixed(1)} MB</span></>
              ) : (
                <><strong>Click to select</strong> or drag &amp; drop<br /><span style={{ color: c.textFaint }}>Post Collections export, .csv — compressed before sending</span></>
              )}
            </div>
          </div>

          {stage && (
            <div style={s.progressWrap}>
              <div style={s.progressTrack}><div style={{ ...s.progressFill, width: stage === 'uploading' ? `${progress}%` : '100%', opacity: stage === 'uploading' ? 1 : 0.4 }} /></div>
              <span style={s.progressLabel}>{stageText}</span>
            </div>
          )}

          {failure && (
            <div style={s.errBlock} role="alert">
              <div style={s.errHead}><AlertTriangle size={14} /> {failure.error}</div>
              {failure.details?.length > 0 && (
                <ul style={s.warnList}>{failure.details.map((d) => <li key={d} style={s.errItem}>{d}</li>)}</ul>
              )}
            </div>
          )}

          <button style={{ ...s.primaryBtn, opacity: file && !busy ? 1 : 0.5 }} disabled={!file || busy}
                  onClick={() => uploadMutation.mutate()}>
            {busy ? 'Working…' : 'Read & Preview'}
          </button>
        </div>
      )}

      {p && (
        <div style={s.card}>
          <div style={s.previewHead}>
            <FileSpreadsheet size={18} color={c.navy} />
            <span style={s.previewTitle}>Preview — {p.file_name}</span>
            <span style={s.previewRange}>{fmtDay(p.first_day)} – {fmtDay(p.last_day)}</span>
          </div>

          {/* What taking the file apart did: the reason this upload exists. */}
          <div style={s.statGrid}>
            <Stat label="Rows in file" value={fmtNumber(p.source_rows)} />
            <Stat label="Site-days" value={fmtNumber(p.site_days)} />
            <Stat label="Tickets" value={fmtNumber(p.pieces.tickets)} />
            <Stat label="Bale counts" value={fmtNumber(p.pieces.bales)} />
            <Stat label="Labor rows" value={fmtNumber(p.pieces.labor)} />
            <Stat label="Line rows" value={fmtNumber(p.pieces.lines)} />
          </div>
          <div style={s.statGrid}>
            <Stat label="Inbound tons" value={fmtTons(p.totals.inbound_tons)} />
            <Stat label="Outbound tons" value={fmtTons(p.totals.outbound_tons)} />
            <Stat label="Bales" value={fmtNumber(p.totals.bales)} />
            <Stat label="Labor hours" value={fmtTons(p.totals.labor_hours)} />
          </div>

          {p.warnings?.length > 0 && (
            <div style={s.warnBlock}>
              <div style={s.warnHead}><AlertTriangle size={14} /> Read before confirming</div>
              <ul style={s.warnList}>{p.warnings.map((w) => <li key={w} style={s.warnItem}>{w}</li>)}</ul>
            </div>
          )}

          <div style={s.subBlock}>
            <div style={s.subLabel}>By site</div>
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>{['Site', 'Days', 'Tickets', 'Inbound tons', 'Outbound tons', 'Bales', 'Labor hours'].map((h, i) => <th key={h} style={i ? s.thR : s.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {p.sites.map((x) => (
                    <tr key={x.site} style={s.tr}>
                      <td style={s.td}>{x.name || `Site ${x.site} (unmapped)`} <span style={{ color: c.textFaint }}>{x.site}</span></td>
                      <td style={s.tdR}>{fmtNumber(x.days)}</td>
                      <td style={s.tdR}>{fmtNumber(x.tickets)}</td>
                      <td style={s.tdR}>{fmtTons(x.inbound_tons)}</td>
                      <td style={s.tdR}>{fmtTons(x.outbound_tons)}</td>
                      <td style={s.tdR}>{fmtNumber(x.bales)}</td>
                      <td style={s.tdR}>{fmtTons(x.labor_hours)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={s.actionRow}>
            <button style={{ ...s.primaryBtn, opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={() => confirmMutation.mutate()}>
              <CheckCircle size={15} /> {confirmMutation.isPending ? 'Committing…' : p.replaces_count ? 'Confirm & Replace' : 'Confirm & Commit'}
            </button>
            <button style={{ ...s.secondaryBtn, opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={() => cancelMutation.mutate()}>
              <XCircle size={15} /> Cancel
            </button>
          </div>
        </div>
      )}

      <BaleWeights />

      <div style={s.logSection}>
        <div style={s.logTitle}>Recent Imports · <Link to="/post-collections">Open the Daily Productivity Report</Link></div>
        {batchesLoading ? <LoadingSpinner /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>{['File', 'Status', 'Days in file', 'Site-days', 'Site-days held', 'Rows', 'By', 'When'].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} style={s.tr}>
                    <td style={s.td} title={b.file_name}>{b.file_name?.slice(-44)}</td>
                    <td style={s.td}><span style={{ ...s.statusPill, ...(STATUS_STYLE[b.status] || STATUS_STYLE.preview) }}>{b.status}</span></td>
                    <td style={s.td}>{b.first_day ? `${fmtDay(b.first_day)} – ${fmtDay(b.last_day)}` : '—'}</td>
                    <td style={s.tdR}>{fmtNumber(b.site_day_count)}</td>
                    <td style={s.tdR}>{b.status === 'committed' ? (b.site_days_held ? fmtNumber(b.site_days_held) : 'none — replaced') : '—'}</td>
                    <td style={s.tdR}>{fmtNumber(b.source_row_count)}</td>
                    <td style={s.td}>{b.uploaded_by_name || '—'}</td>
                    <td style={s.td}>{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
                {!batches.length && <tr><td colSpan={8} style={s.emptyLog}>No imports yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// The weights bale counts are turned into tons with: the Bale_Weights query
// as CSV, replaced whole. Shown back in full for the two report sites, so
// whoever uploaded can see what the report now multiplies by.
function BaleWeights() {
  const qc = useQueryClient();
  const ref = useRef(null);
  const [failure, setFailure] = useState(null);
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['post-collections-bale-weights'],
    queryFn:  () => api.get('/post-collections/bale-weights'),
  });
  const mutation = useMutation({
    mutationFn: (file) => {
      setFailure(null);
      const fd = new FormData();
      fd.append('file', file, file.name);
      return api.upload('/post-collections/bale-weights', fd, () => {});
    },
    onSuccess: (data) => {
      toast.success(`Bale weights replaced: ${fmtNumber(data.after)} materials (was ${fmtNumber(data.before)}).`);
      for (const key of ['post-collections-bale-weights', 'post-collections-report']) qc.invalidateQueries({ queryKey: [key] });
    },
    onError: (err) => setFailure({ error: err.message || 'Upload failed.', details: err.data?.details || [] }),
    onSettled: () => { if (ref.current) ref.current.value = ''; },
  });
  const shown = rows.filter((r) => r.company_code === '03' || r.company_code === '04');
  const uploadedAt = rows.reduce((m, r) => (!m || r.uploaded_at > m ? r.uploaded_at : m), null);

  return (
    <div style={s.card}>
      <div style={{ ...s.previewHead, justifyContent: 'space-between' }}>
        <span style={s.previewTitle}>Bale weights</span>
        <span>
          <input ref={ref} id="bale-weights-file" type="file" accept=".csv" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files[0]; if (f) mutation.mutate(f); }} />
          <button type="button" style={{ ...s.secondaryBtn, opacity: mutation.isPending ? 0.5 : 1 }}
                  disabled={mutation.isPending} onClick={() => ref.current?.click()}>
            <UploadCloud size={15} /> {mutation.isPending ? 'Replacing…' : rows.length ? 'Replace weights' : 'Upload weights'}
          </button>
        </span>
      </div>
      <p style={s.recatText}>
        Bale counts become tons through the average weight per bale of each material, from the Bale_Weights query exported as CSV.
        An upload replaces every weight, and the report re-reads them for every day it shows.
        {uploadedAt && <> Loaded {new Date(uploadedAt).toLocaleString()}{rows[0]?.uploaded_by_name ? ` by ${rows[0].uploaded_by_name}` : ''}.</>}
      </p>
      {failure && (
        <div style={s.errBlock} role="alert">
          <div style={s.errHead}><AlertTriangle size={14} /> {failure.error}</div>
          {failure.details?.length > 0 && <ul style={s.warnList}>{failure.details.map((d) => <li key={d} style={s.errItem}>{d}</li>)}</ul>}
        </div>
      )}
      {isLoading ? <LoadingSpinner /> : shown.length ? (
        <div style={{ ...s.tableWrap, maxHeight: '280px' }}>
          <table style={s.table}>
            <thead><tr>{['Site', 'Material', 'Description', 'Bales', 'Net lbs', 'Lbs / bale'].map((h, i) => <th key={h} style={i > 2 ? s.thR : s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={`${r.company_code}|${r.material_code}`} style={s.tr}>
                  <td style={s.td}>{r.company_code === '04' ? 'Irwindale' : 'Sun Valley'}</td>
                  <td style={s.td}>{r.material_code}</td>
                  <td style={s.td}>{r.material_desc || '—'}</td>
                  <td style={s.tdR}>{fmtNumber(r.bale_count)}</td>
                  <td style={s.tdR}>{fmtNumber(r.net_weight_lbs)}</td>
                  <td style={s.tdR}>{fmtTons(r.weight_per_bale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <div style={s.emptyLog}>No bale weights loaded. Until they are, baled commodities read as zero tons.</div>}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={s.stat}>
      <div style={s.statVal}>{value}</div>
      <div style={s.statLabel}>{label}</div>
    </div>
  );
}

const STATUS_STYLE = {
  committed: { color: c.successText, background: c.successTint },
  preview:   { color: c.info,        background: c.infoTint },
  cancelled: { color: c.textFaint,   background: c.canvas },
};

const s = {
  page:        { maxWidth: '1040px', margin: '0 auto', paddingBottom: '8px' },
  card:        { ...card.sm, padding: '16px', marginBottom: '14px' },
  dropZone:    { borderWidth: '2px', borderStyle: 'dashed', borderRadius: '10px', padding: '30px', textAlign: 'center', cursor: 'pointer', marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' },
  dropText:    { fontSize: '12.5px', color: c.textStrong, lineHeight: 1.6 },
  progressWrap:{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
  progressTrack:{ flex: 1, height: '5px', background: c.border, borderRadius: '4px', overflow: 'hidden' },
  progressFill:{ height: '100%', background: c.navy, borderRadius: '4px', transition: 'width 0.2s' },
  progressLabel:{ fontSize: '10.5px', fontWeight: 600, color: c.textStrong, minWidth: '130px' },
  primaryBtn:  { ...btn.primary },
  secondaryBtn:{ ...btn.secondary },
  previewHead: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  previewTitle:{ fontSize: '13px', fontWeight: 600, color: c.text },
  previewRange:{ fontSize: '12px', color: c.textMuted },
  statGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '12px' },
  stat:        { ...card.sunk, padding: '11px 13px' },
  statVal:     { fontSize: '18px', fontWeight: 700, color: c.text, fontVariantNumeric: 'tabular-nums' },
  statLabel:   { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, marginTop: '3px' },
  subBlock:    { marginBottom: '18px' },
  subLabel:    { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, marginBottom: '8px' },
  warnBlock:   { background: c.warningTint, border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px 14px', margin: '6px 0 18px' },
  warnHead:    { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: c.warningText, marginBottom: '5px' },
  warnList:    { margin: 0, paddingLeft: '18px' },
  warnItem:    { fontSize: '11.5px', color: c.warningText, lineHeight: 1.6 },
  errBlock:    { background: c.dangerTint, border: '1px solid #FCA5A5', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px' },
  errHead:     { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: c.dangerText },
  errItem:     { fontSize: '11.5px', color: c.dangerText, lineHeight: 1.6 },
  tableWrap:   { border: `1px solid ${c.border}`, borderRadius: '8px', overflow: 'auto' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '11.5px' },
  th:          { padding: '8px 10px', background: c.surfaceSunk, borderBottom: `1px solid ${c.border}`, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, whiteSpace: 'nowrap', textAlign: 'left' },
  thR:         { padding: '8px 10px', background: c.surfaceSunk, borderBottom: `1px solid ${c.border}`, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, whiteSpace: 'nowrap', textAlign: 'right' },
  tr:          { borderBottom: `1px solid ${c.canvas}` },
  td:          { padding: '7px 10px', color: c.textStrong, whiteSpace: 'nowrap', borderBottom: `1px solid ${c.canvas}` },
  tdR:         { padding: '7px 10px', color: c.textStrong, whiteSpace: 'nowrap', textAlign: 'right', borderBottom: `1px solid ${c.canvas}`, fontVariantNumeric: 'tabular-nums' },
  actionRow:   { display: 'flex', gap: '10px', marginTop: '6px' },
  logSection:  { ...card.sm, overflow: 'hidden' },
  logTitle:    { fontSize: '13px', fontWeight: 600, color: c.text, padding: '13px 16px', borderBottom: `1px solid ${c.border}`, background: c.surfaceSunk },
  statusPill:  { display: 'inline-flex', padding: '2px 8px', borderRadius: '10px', fontSize: '10.5px', fontWeight: 500 },
  emptyLog:    { textAlign: 'center', padding: '24px', color: c.textFaint },
  recatText:   { fontSize: '12.5px', color: c.textMuted, lineHeight: 1.5, margin: '0 0 12px' },
};
